import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon, Whatshot as TrendingIcon } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { giphyService } from '../services/giphyService';

const GiphySearchModal = ({ open, onClose, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedGif, setSelectedGif] = useState(null);
  const [showTrending, setShowTrending] = useState(true);

  useEffect(() => {
    if (open && !giphyService.isConfigured()) {
      setError('La clé API Giphy n\'est pas configurée. Veuillez ajouter votre clé API dans giphyService.js');
      return;
    }

    if (open && showTrending) {
      loadTrendingGifs();
    }
  }, [open]);

  const loadTrendingGifs = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await giphyService.getTrendingGifs(20);
      setGifs(data.gifs);
      setShowTrending(true);
    } catch (err) {
      setError('Erreur lors du chargement des GIFs tendances');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!searchQuery.trim()) {
      loadTrendingGifs();
      return;
    }

    try {
      setLoading(true);
      setError('');
      setShowTrending(false);
      const data = await giphyService.searchGifs(searchQuery, 20);
      setGifs(data.gifs);
      
      if (data.gifs.length === 0) {
        setError('Aucun GIF trouvé pour cette recherche');
      }
    } catch (err) {
      setError('Erreur lors de la recherche');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGif = (gif) => {
    setSelectedGif(gif);
  };

  const handleConfirm = () => {
    if (selectedGif) {
      onSelect(selectedGif);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setGifs([]);
    setSelectedGif(null);
    setShowTrending(true);
    setError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <img 
              src="https://giphy.com/static/img/giphy-logo-squares.gif" 
              alt="Giphy" 
              style={{ height: 30 }}
            />
            <Typography variant="h6">
              {showTrending ? 'GIFs Tendances' : 'Rechercher des GIFs'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSearch} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            placeholder="Rechercher des GIFs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={!giphyService.isConfigured()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {!showTrending && (
            <Button
              startIcon={<TrendingIcon />}
              onClick={loadTrendingGifs}
              sx={{ mt: 1 }}
              size="small"
            >
              Voir les tendances
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : gifs.length > 0 ? (
          <ImageList cols={3} gap={8}>
            {gifs.map((gif) => (
              <ImageListItem 
                key={gif.id}
                sx={{
                  cursor: 'pointer',
                  border: selectedGif?.id === gif.id ? '3px solid #1976d2' : '3px solid transparent',
                  borderRadius: 1,
                  overflow: 'hidden',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                onClick={() => handleSelectGif(gif)}
              >
                <img
                  src={gif.preview}
                  alt={gif.title}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
                {selectedGif?.id === gif.id && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                    }}
                  >
                    ✓
                  </Box>
                )}
              </ImageListItem>
            ))}
          </ImageList>
        ) : (
          !error && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <Typography color="text.secondary">
                Recherchez des GIFs ou consultez les tendances
              </Typography>
            </Box>
          )
        )}

        {selectedGif && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Sélectionné: {selectedGif.title}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 1 }}>
          Powered by GIPHY
        </Typography>
        <Button onClick={handleClose}>Annuler</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained"
          disabled={!selectedGif}
        >
          Sélectionner
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GiphySearchModal;


